// src/ui/components/readinessRadar.ts — Pure SVG Bexley Readiness Radar Chart (FR-DASH-001, FR-DASH-002)
// Visualizes 4-axis 11+ domain performance against Quest Assessments qualifying benchmarks.

import { tokens } from '@ui/designTokens';
import {
  ReadinessEngine,
  type BexleyReadinessReport,
  type DomainReadiness,
  type ReadinessBand,
} from '@storage/readinessEngine';

export interface ReadinessRadarOptions {
  report?: BexleyReadinessReport;
  size?: number; // SVG viewBox width & height (default 400)
}

export class ReadinessRadar {
  private container: HTMLElement;
  private report: BexleyReadinessReport;
  private size: number;

  constructor(options: ReadinessRadarOptions = {}) {
    this.container = document.createElement('div');
    this.container.id = 'bexley-readiness-radar';
    this.container.className = 'bexley-readiness-deck';
    this.size = options.size ?? 400;
    this.report = options.report ?? ReadinessEngine.calculateReport();
  }

  setReport(report: BexleyReadinessReport): void {
    this.report = report;
    this.render();
  }

  render(): HTMLElement {
    this.container.innerHTML = `
      <div style="display: flex; flex-direction: column; gap: 24px;">
        <!-- Top Aggregate Status Banner -->
        ${this.renderAggregateBanner()}

        <!-- Main Chart & Domains Stage -->
        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); gap: 24px; align-items: center;">
          <!-- SVG Radar Chart Container -->
          <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 20px; background: rgba(255, 253, 249, 0.98); border: 1.5px solid ${tokens.color.gildedBrass}; border-radius: 16px; box-shadow: 0 4px 20px rgba(10, 25, 47, 0.08);">
            <div style="font-size: 0.85rem; font-weight: 800; color: ${tokens.color.seaweedSage}; text-transform: uppercase; margin-bottom: 8px;">
              🎯 4-Axis Domain Radar
            </div>
            ${this.renderSvgRadar()}
            <div style="display: flex; gap: 16px; font-size: 0.8rem; font-weight: 700; color: ${tokens.color.morrisIndigo}; margin-top: 12px; flex-wrap: wrap; justify-content: center;">
              <span style="display: inline-flex; align-items: center; gap: 6px;">
                <span style="display: inline-block; width: 12px; height: 12px; background: rgba(0, 229, 255, 0.4); border: 2px solid ${tokens.color.morrisIndigo}; border-radius: 2px;"></span>
                Tudor's Current SAS
              </span>
              <span style="display: inline-flex; align-items: center; gap: 6px;">
                <span style="display: inline-block; width: 12px; height: 2px; border-top: 2px dashed ${tokens.color.gildedBrass};"></span>
                Bexley Benchmark (108)
              </span>
            </div>
          </div>

          <!-- Domain Performance Cards -->
          <div style="display: flex; flex-direction: column; gap: 12px;">
            ${this.renderDomainCard(this.report.domains['numerical']!)}
            ${this.renderDomainCard(this.report.domains['verbal']!)}
            ${this.renderDomainCard(this.report.domains['reading']!)}
            ${this.renderDomainCard(this.report.domains['non_verbal']!)}
          </div>
        </div>
      </div>
    `;

    return this.container;
  }

  private renderAggregateBanner(): string {
    const { aggregateSas, qualifyingThreshold, isQualifyingProjected, paper1Sas, paper2Sas } = this.report;

    return `
      <div class="morris-card ${isQualifyingProjected ? 'morris-card-gold' : ''}" style="padding: 24px 28px; border-left: 6px solid ${isQualifyingProjected ? tokens.color.seaweedSage : tokens.color.madderCoral};">
        <div style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 16px;">
          <div>
            <div style="font-size: 0.85rem; font-weight: 800; color: ${tokens.color.seaweedSage}; text-transform: uppercase; letter-spacing: 0.5px;">
              🏛️ Quest Assessments · Bexley Selection Standard
            </div>
            <h2 style="font-family: ${tokens.font.heading}; font-size: 1.85rem; color: ${tokens.color.morrisIndigo}; margin: 4px 0 6px;">
              Estimated Aggregate SAS: ${aggregateSas}
            </h2>
            <div style="font-size: 0.95rem; color: #4A5B73;">
              Qualifying Benchmark: <strong>${qualifyingThreshold}</strong> (Historical Bexley Pass Mark)
            </div>
          </div>

          <div style="display: flex; gap: 14px; align-items: center; flex-wrap: wrap;">
            <div style="padding: 10px 16px; background: rgba(255, 253, 249, 0.95); border: 1.5px solid ${tokens.color.gildedBrass}; border-radius: 12px; text-align: center;">
              <span style="font-size: 0.75rem; color: ${tokens.color.seaweedSage}; font-weight: 800; display: block;">PAPER 1 (VERBAL)</span>
              <span style="font-size: 1.3rem; font-weight: 800; color: ${tokens.color.morrisIndigo};">${paper1Sas}</span>
            </div>
            <div style="padding: 10px 16px; background: rgba(255, 253, 249, 0.95); border: 1.5px solid ${tokens.color.gildedBrass}; border-radius: 12px; text-align: center;">
              <span style="font-size: 0.75rem; color: ${tokens.color.seaweedSage}; font-weight: 800; display: block;">PAPER 2 (MATHS/NVR)</span>
              <span style="font-size: 1.3rem; font-weight: 800; color: ${tokens.color.morrisIndigo};">${paper2Sas}</span>
            </div>
            <div style="padding: 10px 18px; border-radius: 14px; font-weight: 800; font-size: 1.05rem; background: ${isQualifyingProjected ? 'rgba(61, 90, 69, 0.15)' : 'rgba(255, 107, 74, 0.15)'}; color: ${isQualifyingProjected ? tokens.color.seaweedSage : tokens.color.madderCoral}; border: 1.5px solid ${isQualifyingProjected ? tokens.color.seaweedSage : tokens.color.madderCoral};">
              ${isQualifyingProjected ? '🌟 Projected Qualifying' : '⚡ Support Recommended'}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private renderDomainCard(domain: DomainReadiness): string {
    const badgeMeta = this.getBadgeMeta(domain.band);
    const diff = domain.estimatedSas - domain.baselineSas;
    const diffText = diff >= 0 ? `+${diff}` : `${diff}`;

    return `
      <div style="padding: 16px 20px; background: rgba(255, 253, 249, 0.95); border: 1.5px solid ${tokens.color.gildedBrass}; border-radius: 12px; box-shadow: 0 2px 8px rgba(7, 20, 36, 0.04);">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px;">
          <h4 style="margin: 0; font-size: 1.05rem; color: ${tokens.color.morrisIndigo}; font-weight: 800;">
            ${domain.name}
          </h4>
          <span style="padding: 4px 10px; border-radius: 12px; font-size: 0.75rem; font-weight: 800; background: ${badgeMeta.bg}; color: ${badgeMeta.color};">
            ${badgeMeta.label}
          </span>
        </div>

        <div style="display: flex; gap: 16px; align-items: center; margin-bottom: 8px; font-size: 0.9rem;">
          <span style="color: ${tokens.color.morrisIndigo}; font-weight: 800; font-size: 1.15rem;">
            SAS ${domain.estimatedSas}
          </span>
          <span style="color: ${diff >= 0 ? tokens.color.seaweedSage : tokens.color.madderCoral}; font-weight: 700;">
            (${diffText} vs baseline ${domain.baselineSas})
          </span>
          <span style="color: #6A7B8C; font-size: 0.85rem;">
            ${domain.totalAttempts} practice items
          </span>
        </div>

        <p style="margin: 0; font-size: 0.85rem; line-height: 1.4; color: #3A4D66; font-style: italic;">
          💡 ${domain.recommendation}
        </p>
      </div>
    `;
  }

  private getBadgeMeta(band: ReadinessBand): { label: string; color: string; bg: string } {
    switch (band) {
      case 'ahead':
        return { label: '🌱 Ahead', color: '#2F4F38', bg: 'rgba(61, 90, 69, 0.18)' };
      case 'on_track':
        return { label: '🌊 On Track', color: '#1A365D', bg: 'rgba(26, 54, 93, 0.12)' };
      case 'support_needed':
        return { label: '⚡ Support Needed', color: '#C93B2B', bg: 'rgba(255, 107, 74, 0.18)' };
    }
  }

  /** Generate pure SVG Radar representation */
  private renderSvgRadar(): string {
    const cx = 200;
    const cy = 200;
    const maxR = 140;

    // 4 axes: 0=Numerical (top), 1=Verbal (right), 2=Reading (bottom), 3=Non-Verbal (left)
    const axesAngles = [
      -Math.PI / 2, // Top
      0,            // Right
      Math.PI / 2,  // Bottom
      Math.PI,      // Left
    ];

    // Scale function: SAS 70 (r=0) to 135 (r=maxR)
    const getRadiusForSas = (sas: number): number => {
      const clamped = Math.max(70, Math.min(135, sas));
      return ((clamped - 70) / (135 - 70)) * maxR;
    };

    // Concentric grid rings
    const ringScores = [85, 100, 108, 115, 130];
    const gridCircles = ringScores.map((score) => {
      const r = getRadiusForSas(score);
      const isBenchmark = score === 108;
      return `
        <circle
          cx="${cx}" cy="${cy}" r="${r}"
          fill="none"
          stroke="${isBenchmark ? tokens.color.gildedBrass : 'rgba(26, 54, 93, 0.15)'}"
          stroke-width="${isBenchmark ? '1.5' : '1'}"
          stroke-dasharray="${isBenchmark ? '4,4' : 'none'}"
        />
        <text
          x="${cx + 4}" y="${cy - r + 11}"
          font-size="9"
          font-family="${tokens.font.mono}"
          fill="${isBenchmark ? tokens.color.gildedBrass : 'rgba(26, 54, 93, 0.4)'}"
          font-weight="${isBenchmark ? '800' : '400'}"
        >${score}</text>
      `;
    }).join('');

    // Axis lines
    const axisLines = axesAngles.map((angle) => {
      const x2 = cx + maxR * Math.cos(angle);
      const y2 = cy + maxR * Math.sin(angle);
      return `<line x1="${cx}" y1="${cy}" x2="${x2}" y2="${y2}" stroke="rgba(26, 54, 93, 0.2)" stroke-width="1.5" />`;
    }).join('');

    // Benchmark polygon (SAS 108)
    const benchmarkRadius = getRadiusForSas(108);
    const benchmarkPoints = axesAngles.map((angle) => {
      const x = cx + benchmarkRadius * Math.cos(angle);
      const y = cy + benchmarkRadius * Math.sin(angle);
      return `${Math.round(x)},${Math.round(y)}`;
    }).join(' ');

    // Tudor's performance points
    const { numerical, verbal, reading, non_verbal } = this.report.domains;
    const scores = [
      numerical?.estimatedSas ?? 112,
      verbal?.estimatedSas ?? 106,
      reading?.estimatedSas ?? 108,
      non_verbal?.estimatedSas ?? 118,
    ];

    const dataPoints = axesAngles.map((angle, i) => {
      const r = getRadiusForSas(scores[i]!);
      const x = cx + r * Math.cos(angle);
      const y = cy + r * Math.sin(angle);
      return { x: Math.round(x), y: Math.round(y), score: scores[i] };
    });

    const polygonPointsString = dataPoints.map((p) => `${p.x},${p.y}`).join(' ');

    // Vertex marker circles
    const vertexMarkers = dataPoints.map((p) => `
      <circle cx="${p.x}" cy="${p.y}" r="5" fill="${tokens.color.morrisIndigo}" stroke="#00E5FF" stroke-width="2" />
    `).join('');

    // Axis Labels
    const labels = [
      { text: 'Numerical (Maths)', x: cx, y: cy - maxR - 12, anchor: 'middle' },
      { text: 'Verbal & SPAG', x: cx + maxR + 10, y: cy + 4, anchor: 'start' },
      { text: 'Reading Comp', x: cx, y: cy + maxR + 20, anchor: 'middle' },
      { text: 'Non-Verbal / Spatial', x: cx - maxR - 10, y: cy + 4, anchor: 'end' },
    ];

    const labelElements = labels.map((l) => `
      <text
        x="${l.x}" y="${l.y}"
        text-anchor="${l.anchor}"
        font-family="${tokens.font.body}"
        font-size="11"
        font-weight="800"
        fill="${tokens.color.morrisIndigo}"
      >${l.text}</text>
    `).join('');

    return `
      <svg
        viewBox="0 0 ${this.size} ${this.size}"
        width="100%"
        max-width="${this.size}px"
        style="overflow: visible; max-width: ${this.size}px;"
        aria-label="Bexley Readiness 4-Axis Radar"
      >
        <defs>
          <radialGradient id="tudorRadarGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stop-color="#00E5FF" stop-opacity="0.45" />
            <stop offset="100%" stop-color="#1A365D" stop-opacity="0.2" />
          </radialGradient>
        </defs>

        <!-- Grid circles & axes -->
        ${gridCircles}
        ${axisLines}

        <!-- Benchmark Ring (108 Qualifying Mark) -->
        <polygon points="${benchmarkPoints}" fill="none" stroke="${tokens.color.gildedBrass}" stroke-width="2" stroke-dasharray="4,4" />

        <!-- Tudor's Performance Polygon -->
        <polygon points="${polygonPointsString}" fill="url(#tudorRadarGlow)" stroke="${tokens.color.morrisIndigo}" stroke-width="2.5" />

        <!-- Data vertices -->
        ${vertexMarkers}

        <!-- Axis Labels -->
        ${labelElements}
      </svg>
    `;
  }

  getElement(): HTMLElement {
    return this.container;
  }
}
